import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../lib/firebase';
import imageCompression from 'browser-image-compression';
import { DEFAULT_COMPRESSION_CONFIG, MIN_SIZE_FOR_COMPRESSION_KB } from '../config/imageCompression';

interface MediaItem {
  url: string;
  description?: string;
  type: 'image' | 'video';
  size?: number;
  name?: string;
}

import { DEFAULT_TRADE_IMAGE_CONFIG, getOptimalImageConfig, getAdaptiveQuality } from '../config/imageOptimization';

// Configuration d'optimisation - QUALITÉ PREMIUM COMME LES GROSSES APPS
const OPTIMIZATION_CONFIG = {
  ...DEFAULT_TRADE_IMAGE_CONFIG,
  
  // Configuration vidéo - OPTIMISÉE POUR LA RAPIDITÉ MAXIMALE
  maxVideoWidth: 640,   // 640x360 (360p) - plus rapide
  maxVideoHeight: 360,
  videoBitrate: 250,    // 250 kbps - ultra agressif
  videoFrameRate: 15,   // 15 FPS - plus rapide
  maxVideoDuration: 30  // Limiter à 30 secondes
};

// Fonction optimisée pour convertir base64 en blob
function base64ToBlob(base64: string): Blob {
  const byteCharacters = atob(base64.split(',')[1]);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const mimeType = base64.split(',')[0].split(':')[1].split(';')[0];
  return new Blob([byteArray], { type: mimeType });
}

// ✅ Compression d'image avec browser-image-compression (qualité professionnelle)
async function compressImage(file: File): Promise<File> {
  try {
    const originalSizeKB = file.size / 1024;
    
    // Ne pas compresser les images déjà petites
    if (originalSizeKB < MIN_SIZE_FOR_COMPRESSION_KB) {
      console.log(`📸 [Compression] Image déjà optimisée (${originalSizeKB.toFixed(0)}KB), skip compression`);
      return file;
    }
    
    console.log(`📸 [Compression] Début: ${originalSizeKB.toFixed(0)}KB`);
    
    // Utiliser la configuration par défaut (haute qualité)
    const compressedFile = await imageCompression(file, DEFAULT_COMPRESSION_CONFIG);
    
    const compressedSizeKB = compressedFile.size / 1024;
    const reduction = ((1 - compressedFile.size / file.size) * 100).toFixed(0);
    
    console.log(`✅ [Compression] Terminée: ${compressedSizeKB.toFixed(0)}KB (-${reduction}%)`);
    
    // Si la compression a augmenté la taille (rare), utiliser l'original
    if (compressedFile.size > file.size) {
      console.log(`⚠️ [Compression] Fichier compressé plus gros, utilisation de l'original`);
      return file;
    }
    
    return compressedFile;
  } catch (error) {
    console.error('❌ [Compression] Échec, utilisation de l\'original:', error);
    return file; // Fallback sécurisé vers l'original
  }
}

// Fonction pour compresser les vidéos - ULTRA RAPIDE
async function compressVideo(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      resolve(blob);
      return;
    }

    video.onloadedmetadata = () => {
      const { videoWidth, videoHeight, duration } = video;
      
      // Limiter la durée pour accélérer
      const maxDuration = Math.min(duration, OPTIMIZATION_CONFIG.maxVideoDuration);
      
      // Calculer les nouvelles dimensions (encore plus petites)
      let newWidth = videoWidth;
      let newHeight = videoHeight;
      
      // Forcer une résolution plus petite pour la rapidité
      if (newWidth > OPTIMIZATION_CONFIG.maxVideoWidth || newHeight > OPTIMIZATION_CONFIG.maxVideoHeight) {
        const ratio = Math.min(OPTIMIZATION_CONFIG.maxVideoWidth / newWidth, OPTIMIZATION_CONFIG.maxVideoHeight / newHeight);
        newWidth = Math.round(newWidth * ratio);
        newHeight = Math.round(newHeight * ratio);
      }
      
      canvas.width = newWidth;
      canvas.height = newHeight;
      
      // Capturer le stream avec un framerate réduit
      const stream = canvas.captureStream(OPTIMIZATION_CONFIG.videoFrameRate);
      
      // Configuration ultra-agressive pour la rapidité
      const options = {
        mimeType: 'video/webm;codecs=vp8', // VP8 plus rapide que VP9
        videoBitsPerSecond: OPTIMIZATION_CONFIG.videoBitrate * 1000
      };
      
      // Fallback
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm';
      }
      
      const mediaRecorder = new MediaRecorder(stream, options);
      const chunks: Blob[] = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const compressedBlob = new Blob(chunks, { type: 'video/webm' });
        const originalSizeMB = blob.size / (1024 * 1024);
        const newSizeMB = compressedBlob.size / (1024 * 1024);
        const reduction = ((originalSizeMB - newSizeMB) / originalSizeMB * 100).toFixed(1);
        
        // console.log supprimé
        resolve(compressedBlob);
      };
      
      mediaRecorder.onerror = () => {
        // console.log supprimé
        resolve(blob);
      };
      
      // Démarrer l'enregistrement
      mediaRecorder.start();
      
      // Variables pour optimiser la vitesse
      let lastFrameTime = 0;
      const frameInterval = 1000 / OPTIMIZATION_CONFIG.videoFrameRate; // Interval entre frames
      
      // Jouer la vidéo plus rapidement
      video.currentTime = 0;
      video.playbackRate = 1.5; // Lecture 1.5x plus rapide
      video.play();
      
      const drawFrame = (currentTime: number) => {
        // Limiter le framerate pour accélérer
        if (currentTime - lastFrameTime >= frameInterval) {
          if (!video.paused && !video.ended && video.currentTime < maxDuration) {
            ctx.drawImage(video, 0, 0, newWidth, newHeight);
            lastFrameTime = currentTime;
          }
        }
        
        if (!video.paused && !video.ended && video.currentTime < maxDuration) {
          requestAnimationFrame(drawFrame);
        } else {
          // Arrêter l'enregistrement
          mediaRecorder.stop();
          stream.getTracks().forEach(track => track.stop());
        }
      };
      
      video.onplay = () => {
        requestAnimationFrame(drawFrame);
      };
      
      // Arrêter automatiquement après la durée maximale
      video.ontimeupdate = () => {
        if (video.currentTime >= maxDuration) {
          video.pause();
          mediaRecorder.stop();
          stream.getTracks().forEach(track => track.stop());
        }
      };
      
      // Timeout de sécurité réduit (15 secondes max)
      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
          stream.getTracks().forEach(track => track.stop());
        }
      }, 15000);
    };
    
    video.onerror = () => {
      // console.log supprimé
      resolve(blob);
    };
    
    // Configurer la vidéo pour la rapidité
    video.muted = true;
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata'; // Chargement plus rapide
    video.src = URL.createObjectURL(blob);
  });
}

// Fonction pour limiter les uploads simultanés
async function limitConcurrency<T>(tasks: Array<() => Promise<T>>, limit: number): Promise<T[]> {
  const results: T[] = [];
  
  // Traiter les tâches par batches de taille `limit`
  for (let i = 0; i < tasks.length; i += limit) {
    const batch = tasks.slice(i, i + limit);
    
    // Exécuter toutes les tâches du batch en parallèle
    const batchResults = await Promise.all(
      batch.map(task => task())
    );
    
    // Ajouter les résultats dans l'ordre
    results.push(...batchResults);
  }
  
  return results;
}

export const mediaStorageService = {
  async uploadTradeMedia(userId: string, tradeId: string, media: string | File | Blob | MediaItem): Promise<MediaItem> {
    try {
      console.log('📤 [MediaStorage] uploadTradeMedia appelé:', {
        userId,
        tradeId,
        mediaType: typeof media,
        isFile: media instanceof File,
        isBlob: media instanceof Blob,
        hasUrl: typeof media === 'object' && 'url' in media
      });
      
      if (!auth.currentUser) {
        console.error('❌ [MediaStorage] Utilisateur non authentifié');
        throw new Error('User not authenticated');
      }

      // ✅ Si c'est un File, l'uploader avec compression intelligente
      if (media instanceof File) {
        console.log('📤 [MediaStorage] Upload du File:', {
          name: media.name,
          type: media.type,
          size: media.size
        });
        
        // ✅ COMPRESSION AUTOMATIQUE pour les images
        let fileToUpload = media;
        if (media.type.startsWith('image/')) {
          console.log('🔄 [MediaStorage] Compression de l\'image...');
          fileToUpload = await compressImage(media);
        }
        
        const timestamp = Date.now();
      const uniqueSuffix = Math.random().toString(36).substring(2, 8);
      // Utiliser le dossier trades avec le bon chemin + suffixe unique pour éviter les collisions
      const storageRef = ref(storage, `trades/${userId}/${tradeId}/${timestamp}_${uniqueSuffix}_${media.name}`);
        console.log('📤 [MediaStorage] Storage ref créé:', storageRef.fullPath);
        
        // Upload du fichier (compressé si image)
        const uploadResult = await uploadBytes(storageRef, fileToUpload);
        console.log('✅ [MediaStorage] Upload terminé:', {
          bytesTransferred: uploadResult.metadata.size,
          fullPath: uploadResult.ref.fullPath
        });
        
        const downloadURL = await getDownloadURL(storageRef);
        console.log('✅ [MediaStorage] URL obtenue:', downloadURL);
        
        return {
          url: downloadURL,
          type: media.type.startsWith('image/') ? 'image' : 'video',
          size: fileToUpload.size,
          name: media.name
        };
      }

      let blob: Blob;
      let mediaType: 'image' | 'video' = 'image';
      let originalSize: number | undefined;
      let originalName: string | undefined;

      // Si c'est déjà un objet MediaItem avec une URL Firebase Storage, le retourner tel quel
      if (typeof media === 'object' && 'url' in media && 'type' in media) {
        if (media.url.includes('firebasestorage.googleapis.com')) {
          console.log('✅ [MediaStorage] Média déjà sur Firebase, retourné tel quel');
          return media;
        }
        
        // Convertir l'URL base64 en blob (optimisé)
        if (media.url.startsWith('data:')) {
          console.log('🔄 [MediaStorage] Conversion base64 -> blob');
          blob = base64ToBlob(media.url);
          mediaType = media.type;
          originalSize = media.size;
          originalName = media.name;
        } else {
          console.error('❌ [MediaStorage] Format URL invalide:', media.url?.substring(0, 50));
          throw new Error('Invalid media URL format');
        }
      }
      // Si c'est un Blob (pas File)
      else if (media instanceof Blob) {
        console.log('✅ [MediaStorage] Blob détecté:', {
          type: media.type,
          size: media.size
        });
        blob = media;
        mediaType = media.type.startsWith('image/') ? 'image' : 'video';
        originalSize = media.size;
      }
      // Si c'est une chaîne base64 (optimisé)
      else if (typeof media === 'string' && media.startsWith('data:')) {
        console.log('🔄 [MediaStorage] String base64 détecté');
        blob = base64ToBlob(media);
        mediaType = media.startsWith('data:image/') ? 'image' : 'video';
      }
      // Si c'est une URL Firebase Storage existante
      else if (typeof media === 'string' && media.includes('firebasestorage.googleapis.com')) {
        console.log('✅ [MediaStorage] URL Firebase existante');
        return {
          url: media,
          type: 'image' // Par défaut, on assume que c'est une image pour la compatibilité
        };
      }
      else {
        console.error('❌ [MediaStorage] Format média invalide:', typeof media);
        throw new Error('Invalid media format');
      }

      // Note: La compression des images est maintenant gérée en amont pour les File objects
      // Ce bloc ne s'applique que pour les anciens formats (base64, Blob sans File)
      if (mediaType === 'image') {
        console.log('📸 [MediaStorage] Image (format legacy):', blob.size, 'bytes');
      }
      
      // Optimiser TOUTES les vidéos avant l'upload - COMPRESSION EFFICACE RÉACTIVÉE
      if (mediaType === 'video') {
        const originalSizeMB = blob.size / (1024 * 1024);
        
        try {
          // console.log supprimé
          
          // Compression vidéo efficace et rapide
          blob = await compressVideo(blob);
          
          const newSizeMB = blob.size / (1024 * 1024);
          const reduction = ((originalSizeMB - newSizeMB) / originalSizeMB * 100).toFixed(1);
          
          // console.log supprimé
        } catch (error) {
          // console.error supprimé
          // En cas d'erreur, utiliser l'original
        }
      }

      // Déterminer l'extension basée sur le type de blob après compression
      const timestamp = Date.now();
      const uniqueSuffix = Math.random().toString(36).substring(2, 8);
      let fileExtension = 'jpg'; // Par défaut JPEG pour de meilleures performances
      
      if (blob.type.includes('png')) {
        fileExtension = 'png';
      } else if (blob.type.includes('webp')) {
        fileExtension = 'webp';
      } else if (blob.type.includes('jpeg') || blob.type.includes('jpg')) {
        fileExtension = 'jpg';
      } else if (blob.type.includes('webm')) {
        fileExtension = 'webm';
      } else if (mediaType === 'video') {
        fileExtension = 'mp4';
      }

      const mediaPath = `trades/${userId}/${tradeId}/${timestamp}_${uniqueSuffix}.${fileExtension}`;
      const storageRef = ref(storage, mediaPath);

      console.log('📤 [MediaStorage] Upload en cours vers:', mediaPath);
      console.log('📤 [MediaStorage] Blob à uploader:', {
        type: blob.type,
        size: blob.size
      });

      // ✅ CORRECTION: Spécifier explicitement le contentType dans les metadata
      const metadata = {
        contentType: blob.type || 'image/jpeg',
        customMetadata: {
          uploadedAt: new Date().toISOString(),
          originalName: originalName || 'unknown'
        }
      };
      
      console.log('📤 [MediaStorage] Metadata:', metadata);
      console.log('📤 [MediaStorage] Storage ref:', {
        bucket: storageRef.bucket,
        fullPath: storageRef.fullPath,
        name: storageRef.name
      });

      // ✅ CORRECTION: Utiliser uploadBytesResumable pour plus de contrôle
      const uploadTask = uploadBytesResumable(storageRef, blob, metadata);
      
      // Attendre que l'upload soit terminé avec une Promise
      const uploadResult = await new Promise<any>((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`📤 [MediaStorage] Progression: ${progress.toFixed(1)}% (${snapshot.bytesTransferred}/${snapshot.totalBytes})`);
            console.log(`📤 [MediaStorage] État: ${snapshot.state}`);
          },
          (error) => {
            console.error('❌ [MediaStorage] Erreur pendant upload:', error.code, error.message);
            reject(error);
          },
          async () => {
            console.log('✅ [MediaStorage] Upload complété !');
            resolve(uploadTask.snapshot);
          }
        );
      });
      
      console.log('✅ [MediaStorage] Upload terminé:', {
        fullPath: uploadResult.ref.fullPath,
        bucket: uploadResult.ref.bucket,
        bytesTransferred: uploadResult.bytesTransferred,
        totalBytes: uploadResult.totalBytes,
        state: uploadResult.state
      });

      // ✅ CORRECTION: Attendre un peu pour la propagation Firebase
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Obtenir l'URL de téléchargement
      console.log('📤 [MediaStorage] Récupération URL...');
      const downloadURL = await getDownloadURL(uploadResult.ref);
      console.log('✅ [MediaStorage] URL obtenue:', downloadURL)

      return {
        url: downloadURL,
        type: mediaType,
        size: blob.size,
        name: originalName
      };

    } catch (error: any) {
      console.error('❌ [MediaStorage] Erreur upload:', error);
      throw error;
    }
  },

  async uploadTradeMedias(userId: string, tradeId: string, medias: Array<string | File | Blob | MediaItem>): Promise<(MediaItem | null)[]> {
    if (!medias || !Array.isArray(medias)) {
      // console.log supprimé
      return [];
    }

    if (!userId || !tradeId) {
      throw new Error('User ID and Trade ID are required for uploading media');
    }

    // console.log supprimé

    // Créer les tâches d'upload
    const uploadTasks = medias.map((media, index) => async () => {
      try {
        // console.log supprimé
        const result = await this.uploadTradeMedia(userId, tradeId, media);
        // console.log supprimé
        return result;
      } catch (error) {
        // console.error supprimé
        // Au lieu de retourner null, on va essayer de continuer avec les autres
        return null;
      }
    });

    // Exécuter avec limitation de concurrence
    // console.log supprimé
    const results = await limitConcurrency(uploadTasks, OPTIMIZATION_CONFIG.maxConcurrentUploads);
    
    // ✅ FIX: Retourner les résultats AVEC nulls pour préserver l'alignement des index
    // Les appelants doivent mapper les descriptions AVANT de filtrer les nulls
    return results;
  },

  // Méthodes de compatibilité pour les images (pour ne pas casser le code existant)
  async uploadTradeImage(userId: string, tradeId: string, image: string | File | Blob): Promise<string> {
    const result = await this.uploadTradeMedia(userId, tradeId, image);
    return result.url;
  },

  async uploadTradeImages(userId: string, tradeId: string, images: Array<string | File | Blob | { url: string, description?: string }>): Promise<{ url: string, description?: string }[]> {
    if (!images || !Array.isArray(images)) {
      return [];
    }

    if (!userId || !tradeId) {
      throw new Error('User ID and Trade ID are required for uploading images');
    }

    // Créer les tâches d'upload
    const uploadTasks = images.map((image) => async () => {
      try {
        const result = await this.uploadTradeMedia(userId, tradeId, typeof image === 'object' && 'url' in image ? image.url : image);
        
        return {
          url: result.url,
          description: typeof image === 'object' && 'description' in image ? image.description : undefined
        };
      } catch (error) {
        // console.error supprimé
        return null;
      }
    });

    // Exécuter avec limitation de concurrence
    const results = await limitConcurrency(uploadTasks, OPTIMIZATION_CONFIG.maxConcurrentUploads);
    const validResults: { url: string, description?: string }[] = [];
    
    for (const result of results) {
      if (result !== null) {
        validResults.push(result);
      }
    }
    
    return validResults;
  },

  // Fonction pour supprimer un fichier média de Firebase Storage
  async deleteMediaFromStorage(mediaUrl: string): Promise<void> {
    try {
      if (!mediaUrl || !mediaUrl.includes('firebasestorage.googleapis.com')) {
        // console.warn supprimé
        return;
      }

      // Extraire le chemin du fichier depuis l'URL Firebase Storage
      const url = new URL(mediaUrl);
      const pathMatch = url.pathname.match(/\/o\/(.+)$/);
      if (!pathMatch) {
        // console.warn supprimé
        return;
      }

      const filePath = decodeURIComponent(pathMatch[1]);
      // console.log supprimé

      // Créer une référence au fichier
      const fileRef = ref(storage, filePath);

      // Supprimer le fichier
      await deleteObject(fileRef);
      // console.log supprimé

    } catch (error: any) {
      // console.error supprimé
      // Ne pas faire échouer l'opération si le fichier n'existe pas ou est déjà supprimé
      if (error.code === 'storage/object-not-found') {
        // console.warn supprimé
      } else {
        throw error;
      }
    }
  },

  // Fonction pour supprimer plusieurs fichiers médias
  async deleteMultipleMediaFromStorage(mediaUrls: string[]): Promise<void> {
    if (!mediaUrls || mediaUrls.length === 0) {
      return;
    }

    // console.log supprimé

    // Supprimer tous les fichiers en parallèle
    const deletionPromises = mediaUrls.map(url => 
      this.deleteMediaFromStorage(url).catch(error => {
        // console.error supprimé
        return null; // Continuer avec les autres même si l'un échoue
      })
    );

    await Promise.all(deletionPromises);
    // console.log supprimé
  }
}; 
